import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { endOfWeek, isAfter, isSameDay, isWithinInterval, startOfWeek } from 'date-fns';
import { ru } from 'date-fns/locale';
import { styled } from '@mui/material/styles';
import React, { FC, ReactElement, useState } from 'react';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';

interface Props {
  open: boolean;
  onRange: (startDate: Date | null, endDate: Date | null) => void;
  onClose: () => void;
}

type CustomPickerDayProps = PickersDayProps & {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
};

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
})<CustomPickerDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...{ width: '40px' },
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(isLastDay && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})) as React.ComponentType<CustomPickerDayProps>;

export const DateRangePicker: FC<Props> = (props: Props) => {
  const { open, onRange, onClose } = props;
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isStartDateSelected, setStartDateSelected] = useState<boolean>(false);
  const handleChange = (date: Date | null): void => {
    if (!!startDate && isStartDateSelected) {
      if (!!date && isAfter(startDate, date)) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      setStartDateSelected(false);
    } else {
      if (!!date && !!endDate && isAfter(date, endDate)) {
        setStartDate(endDate);
        setEndDate(date);
      } else {
        setStartDate(date);
      }
      setStartDateSelected(true);
    }
  };
  const reset = (): void => {
    setStartDate(null);
    setEndDate(null);
    setStartDateSelected(false);
  };

  const DaySlot = (pickersDayProps: PickersDayProps): ReactElement => {
    const { day } = pickersDayProps;
    if (!startDate || !endDate) {
      return (
        <CustomPickersDay
          {...pickersDayProps}
          disableMargin={true}
          dayIsBetween={false}
          isFirstDay={false}
          isLastDay={false}
        />
      );
    }
    const dayIsBetween: boolean =
      isSameDay(day, startDate) ||
      isSameDay(day, endDate) ||
      isWithinInterval(day, {
        start: startDate,
        end: endDate,
      });
    const isFirstDay: boolean = isSameDay(day, startDate) || isSameDay(day, startOfWeek(day, { locale: ru }));
    const isLastDay: boolean = isSameDay(day, endDate) || isSameDay(day, endOfWeek(day, { locale: ru }));
    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin={true}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <MobileDatePicker
      open={open}
      views={['day']}
      reduceAnimations={false}
      slots={{ day: DaySlot, toolbar: () => null }}
      value={isStartDateSelected ? startDate : endDate}
      minDate={isStartDateSelected && startDate ? startDate : undefined}
      maxDate={!isStartDateSelected && endDate ? endDate : undefined}
      slotProps={{
        textField: {
          sx: { position: 'absolute', visibility: 'hidden' },
        },
      }}
      onChange={handleChange}
      onAccept={() => {
        onRange(startDate, endDate);
        reset();
      }}
      onClose={() => {
        onClose();
        reset();
      }}
    />
  );
};
